import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, Settings, Cookie, FileText, ExternalLink } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

interface ConsentSettings {
  enabled: boolean;
  version: number;
  banner: {
    title: string;
    description: string;
    acceptAllText: string;
    rejectAllText: string;
    customizeText: string;
    privacyPolicyUrl?: string;
    cookiePolicyUrl?: string;
  };
  categories: {
    id: string;
    name: string;
    description: string;
    required: boolean;
    defaultEnabled: boolean;
    cookies: string[];
    scripts: string[];
  }[];
  styling: {
    position: 'bottom' | 'top' | 'center';
    theme: 'light' | 'dark' | 'auto';
    primaryColor?: string;
  };
}

const defaultSettings: ConsentSettings = {
  enabled: false,
  version: 1,
  banner: {
    title: 'We value your privacy',
    description:
      'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept All", you consent to our use of cookies.',
    acceptAllText: 'Accept All',
    rejectAllText: 'Reject All',
    customizeText: 'Customize',
    privacyPolicyUrl: '',
    cookiePolicyUrl: '',
  },
  categories: [
    {
      id: 'necessary',
      name: 'Necessary',
      description: 'Essential cookies required for the website to function properly.',
      required: true,
      defaultEnabled: true,
      cookies: [],
      scripts: [],
    },
    {
      id: 'analytics',
      name: 'Analytics',
      description: 'Cookies that help us understand how visitors interact with our website.',
      required: false,
      defaultEnabled: false,
      cookies: [],
      scripts: [],
    },
    {
      id: 'marketing',
      name: 'Marketing',
      description: 'Cookies used to track visitors across websites for advertising purposes.',
      required: false,
      defaultEnabled: false,
      cookies: [],
      scripts: [],
    },
    {
      id: 'personalization',
      name: 'Personalization',
      description: 'Cookies that enable personalized features and content.',
      required: false,
      defaultEnabled: false,
      cookies: [],
      scripts: [],
    },
  ],
  styling: {
    position: 'bottom',
    theme: 'auto',
    primaryColor: '#3b82f6',
  },
};

export default function ConsentPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['consent-settings', projectId],
    queryFn: async () => {
      const response = await api.get(`/projects/${projectId}/consent/settings`);
      return response.data;
    },
    enabled: Boolean(projectId),
  });

  const [settings, setSettings] = useState<ConsentSettings>(settingsData?.data || defaultSettings);

  const saveSettingsMutation = useMutation({
    mutationFn: async (data: Partial<ConsentSettings>) => {
      await api.put(`/projects/${projectId}/consent/settings`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['consent-settings', projectId] });
      toast.success('Consent settings saved');
    },
    onError: () => {
      toast.error('Failed to save settings');
    },
  });

  const handleSave = () => {
    saveSettingsMutation.mutate(settings);
  };

  const updateBanner = (field: string, value: string) => {
    setSettings((prev) => ({
      ...prev,
      banner: {
        ...prev.banner,
        [field]: value,
      },
    }));
  };

  const updateStyling = (field: string, value: string) => {
    setSettings((prev) => ({
      ...prev,
      styling: {
        ...prev.styling,
        [field]: value,
      },
    }));
  };

  const updateCategory = (id: string, field: string, value: boolean | string) => {
    setSettings((prev) => ({
      ...prev,
      categories: prev.categories.map((cat) => (cat.id === id ? { ...cat, [field]: value } : cat)),
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cookie Consent</h1>
          <p className="text-muted-foreground">
            Configure GDPR-compliant cookie consent for your website
          </p>
        </div>
        <Button onClick={handleSave} disabled={saveSettingsMutation.isPending}>
          {saveSettingsMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      {/* Enable/Disable Toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Cookie Consent Banner</CardTitle>
              <CardDescription>
                Enable or disable the cookie consent banner on your website
              </CardDescription>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings((prev) => ({ ...prev, enabled: checked }))}
            />
          </div>
        </CardHeader>
      </Card>

      <Tabs defaultValue="banner" className="space-y-4">
        <TabsList>
          <TabsTrigger value="banner">
            <FileText className="mr-2 h-4 w-4" />
            Banner Text
          </TabsTrigger>
          <TabsTrigger value="categories">
            <Cookie className="mr-2 h-4 w-4" />
            Categories
          </TabsTrigger>
          <TabsTrigger value="styling">
            <Settings className="mr-2 h-4 w-4" />
            Styling
          </TabsTrigger>
        </TabsList>

        {/* Banner Text Tab */}
        <TabsContent value="banner" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Banner Content</CardTitle>
              <CardDescription>
                Customize the text displayed in your cookie consent banner
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={settings.banner.title}
                  onChange={(e) => updateBanner('title', e.target.value)}
                  placeholder="We value your privacy"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={settings.banner.description}
                  onChange={(e) => updateBanner('description', e.target.value)}
                  placeholder="Describe how you use cookies..."
                  rows={3}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="acceptAllText">Accept All Button</Label>
                  <Input
                    id="acceptAllText"
                    value={settings.banner.acceptAllText}
                    onChange={(e) => updateBanner('acceptAllText', e.target.value)}
                    placeholder="Accept All"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rejectAllText">Reject All Button</Label>
                  <Input
                    id="rejectAllText"
                    value={settings.banner.rejectAllText}
                    onChange={(e) => updateBanner('rejectAllText', e.target.value)}
                    placeholder="Reject All"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="customizeText">Customize Button</Label>
                  <Input
                    id="customizeText"
                    value={settings.banner.customizeText}
                    onChange={(e) => updateBanner('customizeText', e.target.value)}
                    placeholder="Customize"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Policy Links</CardTitle>
              <CardDescription>Link to your privacy and cookie policies</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="privacyPolicyUrl">Privacy Policy URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="privacyPolicyUrl"
                    value={settings.banner.privacyPolicyUrl || ''}
                    onChange={(e) => updateBanner('privacyPolicyUrl', e.target.value)}
                    placeholder="https://example.com/privacy"
                  />
                  {settings.banner.privacyPolicyUrl && (
                    <Button variant="outline" size="icon" asChild>
                      <a
                        href={settings.banner.privacyPolicyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cookiePolicyUrl">Cookie Policy URL</Label>
                <div className="flex gap-2">
                  <Input
                    id="cookiePolicyUrl"
                    value={settings.banner.cookiePolicyUrl || ''}
                    onChange={(e) => updateBanner('cookiePolicyUrl', e.target.value)}
                    placeholder="https://example.com/cookies"
                  />
                  {settings.banner.cookiePolicyUrl && (
                    <Button variant="outline" size="icon" asChild>
                      <a
                        href={settings.banner.cookiePolicyUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="space-y-4">
          {settings.categories.map((category) => (
            <Card key={category.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {category.name}
                      {category.required && <Shield className="text-muted-foreground h-4 w-4" />}
                    </CardTitle>
                    <CardDescription>{category.description}</CardDescription>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`${category.id}-default`} className="text-sm">
                        Default Enabled
                      </Label>
                      <Switch
                        id={`${category.id}-default`}
                        checked={category.defaultEnabled}
                        onCheckedChange={(checked) =>
                          updateCategory(category.id, 'defaultEnabled', checked)
                        }
                        disabled={category.required}
                      />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Cookies (comma-separated)</Label>
                    <Input
                      value={category.cookies.join(', ')}
                      onChange={(e) =>
                        updateCategory(
                          category.id,
                          'cookies',
                          e.target.value.split(',').map((s) => s.trim()) as unknown as string
                        )
                      }
                      placeholder="_ga, _gid, _gat"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Scripts (comma-separated)</Label>
                    <Input
                      value={category.scripts.join(', ')}
                      onChange={(e) =>
                        updateCategory(
                          category.id,
                          'scripts',
                          e.target.value.split(',').map((s) => s.trim()) as unknown as string
                        )
                      }
                      placeholder="google-analytics, facebook-pixel"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Styling Tab */}
        <TabsContent value="styling" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Banner Appearance</CardTitle>
              <CardDescription>
                Customize how the consent banner looks on your website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <Select
                    value={settings.styling.position}
                    onValueChange={(value) => updateStyling('position', value)}
                  >
                    <SelectTrigger id="position">
                      <SelectValue placeholder="Select position" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bottom">Bottom</SelectItem>
                      <SelectItem value="top">Top</SelectItem>
                      <SelectItem value="center">Center Modal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="theme">Theme</Label>
                  <Select
                    value={settings.styling.theme}
                    onValueChange={(value) => updateStyling('theme', value)}
                  >
                    <SelectTrigger id="theme">
                      <SelectValue placeholder="Select theme" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="auto">Auto (System)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="primaryColor">Primary Color</Label>
                  <div className="flex gap-2">
                    <Input
                      id="primaryColor"
                      type="color"
                      value={settings.styling.primaryColor || '#3b82f6'}
                      onChange={(e) => updateStyling('primaryColor', e.target.value)}
                      className="h-10 w-14 p-1"
                    />
                    <Input
                      value={settings.styling.primaryColor || '#3b82f6'}
                      onChange={(e) => updateStyling('primaryColor', e.target.value)}
                      placeholder="#3b82f6"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Preview Card */}
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
              <CardDescription>This is how your consent banner will look</CardDescription>
            </CardHeader>
            <CardContent>
              <div
                className={`rounded-lg border p-4 ${
                  settings.styling.theme === 'dark'
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-900'
                }`}
              >
                <h3 className="font-semibold">{settings.banner.title}</h3>
                <p className="mt-2 text-sm opacity-80">{settings.banner.description}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button size="sm" style={{ backgroundColor: settings.styling.primaryColor }}>
                    {settings.banner.acceptAllText}
                  </Button>
                  <Button size="sm" variant="outline">
                    {settings.banner.rejectAllText}
                  </Button>
                  <Button size="sm" variant="ghost">
                    {settings.banner.customizeText}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
